import { Button, Typography } from '@jisr-hr/ds-beta'
import DSIcon from 'components/DSIcon'
import { useEffect } from 'react'
import { useForm, useFormState } from 'react-final-form'
import i18n from 'translations/i18n'
import { useDispatch, useSelector } from 'utils/hooks'
import { loadEmployeePolices } from 'redux/setting/attendanceManagement/attendancePolicy/actionCreators'
import { useUpdateOvertimeRequestNew } from 'apis/Attendance/overtimeRequest/useOvertimeRequest'
import { Loader } from 'components/global/JisrLoader/JisrLoader'
import { CompensationType } from 'apis/Attendance/overtimeRequest/useOvertimeRequest.type'
import { requestDetailsLoad } from 'redux/attendance/timesheets/requestDetails/actionCreators'

import { organizationPolicy } from '../../components/forms/OvertimeDuration/helper'
import HourlyPopper from '../../components/RequestedOvertime/HourlyPopper'
import Frame from '../../ds/Frame'
import RadioWithLabel from './RadioWithLabel'

type EmployeeTypes = {
  avatar_thumb: string
  code: string
  full_name_i18n: string
  id: number
  is_exempted: boolean
  job_title: string
  name: string
  name_i18n: string
}

type OvertimeCompensationPropsT = {
  request: {
    id: number
    total_cost_of_hours: number | null | undefined
    hour_rate: number
    status: string
    can_take_action: boolean
    view_only: boolean
    compensation: CompensationType
    approved_compensation: CompensationType
    employee: EmployeeTypes
    request_type: string
  }
  reloadRequest: () => void
}

const OvertimeCompensation = (props: OvertimeCompensationPropsT) => {
  const { request } = props
  const { change } = useForm()
  const { values } = useFormState()
  const dispatch = useDispatch()

  const { mutate: updateOvertimeRequestNew, isLoading } = useUpdateOvertimeRequestNew()

  const { currency_type_i18n } = useSelector(
    ({ organizationProfile }) => organizationProfile.organization_currency,
  )

  const attendanceEmployeePolicy = useSelector(
    ({ attendancePolicy }) => attendancePolicy.attendanceEmployeePolicy,
  )

  const { employee } = useSelector(({ auth }) => auth)

  const isOvertimeCancellationRequest = request?.request_type === 'OvertimeCancellationRequest'

  const canChangeCompensation = request?.can_take_action && !isOvertimeCancellationRequest

  const canChangeRate =
    !isOvertimeCancellationRequest &&
    request?.status === 'pending' &&
    employee?.permission_scopes?.update_overtime_rate

  const polices = organizationPolicy({
    is_overtime_enabled:
      attendanceEmployeePolicy?.is_overtime_enabled ||
      request.approved_compensation === 'paid_overtime',
    is_time_off_in_lieu_enabled: attendanceEmployeePolicy?.is_time_off_in_lieu_enabled,
  })

  useEffect(() => {
    if (request?.employee?.id)
      dispatch(
        loadEmployeePolices(request.employee.id, {
          policy_type: 'overtime',
          request_id: request.id,
        }),
      )
  }, [request?.employee?.id])

  return (
    <Frame
      title={i18n.t('label.compensation')}
      description={i18n.t('label.modal.compensation.employee_preference')}
    >
      {isLoading && <Loader fixed />}

      <div className="flex flex-col gap-4">
        {polices.map((policy) => {
          return (
            <RadioWithLabel
              key={policy?.id}
              label={policy?.title}
              value={policy?.id}
              isEmployeePreference={request.compensation === policy?.id}
              description={policy?.helperText}
              active={request.approved_compensation === policy?.id}
              disabled={!canChangeCompensation || polices.length === 1}
              onChange={(v) => {
                if (request.approved_compensation !== policy.id) {
                  updateOvertimeRequestNew(
                    {
                      id: request?.id,
                      params: { approved_compensation: v.target.value as CompensationType },
                    },
                    {
                      onSuccess: () => {
                        if (request.employee?.id) {
                          dispatch(
                            requestDetailsLoad(request.employee.id, { request_id: request?.id }),
                          )
                        }
                      },
                    },
                  )
                }
              }}
            />
          )
        })}
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <DSIcon name="clock-timer" />
            <Typography
              text={i18n.t('hourly_rate')}
              variant="subtitle-1"
            />
          </div>
          <div className="flex gap-3 items-center">
            {values.hourly_popper ? (
              <HourlyPopper
                details={request}
                reloadRequest={props.reloadRequest}
              />
            ) : (
              <>
                <Typography
                  text={
                    request.compensation === 'time_off_in_lieu'
                      ? i18n.t('label.hourly_rate_toil_compensation_days', {
                          rate: request?.hour_rate ?? '--',
                        })
                      : `${request?.hour_rate} X`
                  }
                  variant="body-new/bold"
                />
                <Button
                  color="primary"
                  size="small"
                  variant="ghost"
                  onClick={() => {
                    change('hourly_popper', true)
                  }}
                  disabled={!canChangeRate}
                  label={i18n.t('label.hourly_rate_change')}
                />
              </>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center border-0 border-t border-solid  !border-color-border-lighter py-3">
          <div className="flex gap-2">
            <DSIcon name="calendar" />
            <Typography
              text={i18n.t('cost')}
              variant="subtitle-1"
            />
          </div>
          <Typography
            text={
              request.compensation === 'time_off_in_lieu'
                ? i18n.t('label.total_toil_compensation_days', {
                    count: +(request?.total_cost_of_hours ?? 0).toFixed(2),
                  })
                : `${request?.total_cost_of_hours ?? request?.hour_rate ?? '--'} ${
                    request.compensation === 'paid_overtime' ? currency_type_i18n ?? '' : ''
                  }`
            }
            variant="body-new/bold"
          />
        </div>
      </div>
    </Frame>
  )
}

export default OvertimeCompensation
